import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  ContributorStack,
  LinkChip,
  PersonInline,
} from "../QokrPrimitives/QokrPrimitives";
import QokrOkrDetails from "../QokrOkrDetails/QokrOkrDetails";
import "../_vendor/_company-okr-board.scss";
import "./_qokr-objective-row.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One objective as a collapsible row — the Annual OKR board row (`cob__*`):
// number · statement, then a quiet meta line (owner · contributors · link),
// state pill + actions on the right; open = key results and initiatives.
// Every QOKR list uses it (interview board, team board, remarks, summary).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * @param label       "O1" etc. from the BE; falls back to the position.
 * @param link        `{ label, kind, relationship }` — omit for no link chip.
 * @param pill        Node on the right (a StatePill / verdict).
 * @param actions     Extra buttons on the right (Edit, Delete…).
 * @param final       Settled objective — teal title, as on the Annual board.
 * @param isActive    The row the conversation / agenda is on: opens itself
 *                    and scrolls into view.
 */
const QokrObjectiveRow = ({
  index,
  label,
  statement,
  owner,
  contributors = [],
  link = null,
  keyResults = [],
  initiatives = [],
  pill = null,
  actions = null,
  flag = null,
  final = false,
  isActive = false,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const rowRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;
    setOpen(true);
    rowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [isActive]);

  return (
    <li
      ref={rowRef}
      className={classNames("cob__row qokr-row", {
        "cob__row--open": open,
        "cob__row--final": final,
        "qokr-row--active": isActive,
      })}
    >
      <div className="cob__row-head">
        <button
          type="button"
          className="cob__row-main"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          <span className="cob__row-index">{label || index + 1}</span>
          <span className="cob__row-sep" aria-hidden="true">
            .
          </span>
          <span className="cob__row-text">
            <span className="cob__row-statement">{statement}</span>
            <span className="cob__row-meta">
              <PersonInline person={owner} />
              <ContributorStack people={contributors} />
              {link ? (
                <LinkChip
                  label={link.label}
                  kind={link.kind}
                  relationship={link.relationship}
                  focusable={false}
                />
              ) : null}
            </span>
          </span>
        </button>

        <span className="cob__row-actions">
          {pill}
          {actions}
          <button
            type="button"
            className="cob__chevron"
            onClick={() => setOpen((value) => !value)}
            aria-label={open ? "Collapse" : "Expand"}
          >
            <i
              className={`fa-solid ${open ? "fa-chevron-up" : "fa-chevron-down"}`}
              aria-hidden="true"
            />
          </button>
        </span>
      </div>

      {flag ? (
        <div className="qokr-row__flag">
          <i className="fa-solid fa-flag" aria-hidden="true" />
          {flag}
        </div>
      ) : null}

      {open ? (
        <div className="cob__row-body">
          <QokrOkrDetails keyResults={keyResults} initiatives={initiatives} />
        </div>
      ) : null}
    </li>
  );
};

export default QokrObjectiveRow;
